<script setup>
import { ref, computed } from "vue";
import SearchableSelectComponent from "@/components/SearchableSelectComponent.vue";
import useActionButtonsCommon from "@/composables/ActionButtonsCommon";

const { store, loading, _error } = useActionButtonsCommon();

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const device_filter = computed(
  () => store.getters["devicesFilterOptions/getFilterOptions"]
);

const deviceInitialData = {
  tag: "",
  imei: "",
  iccid: "",
  msisdn: "",
  course_id: {},
  device_class_id: {},
  device_type_id: {},
  provider_id: {},
  battery: "100",
};

const formData = ref(deviceInitialData);
const validationErrors = ref({
  tag: false,
  imei: false,
  iccid: false,
  msisdn: false,
  course_id: false,
  device_class_id: false,
  device_type_id: false,
  provider_id: false,
});
const isFormValidated = ref(false);

const courseNames = computed(() => device_filter.value?.courses || []);
const deviceTypes = computed(() => device_filter.value?.deviceTypes || []);
const deviceClasses = computed(() => device_filter.value?.deviceClass || []);
const providers = computed(() => device_filter.value?.providers || []);

function closeModal() {
  _error.value = null;
  emit("update:modelValue", false);
  resetValidation();
}

function validateField(fieldName) {
  if (typeof formData.value[fieldName] === "string") {
    validationErrors.value[fieldName] = formData.value[fieldName].trim() === "";
  } else {
    validationErrors.value[fieldName] =
      Object.keys(formData.value[fieldName]).length === 0;
  }
}

function validateForm() {
  isFormValidated.value = true;

  Object.keys(validationErrors.value).forEach((key) => {
    validateField(key);
  });

  return !Object.values(validationErrors.value).some((value) => value === true);
}

function resetValidation() {
  isFormValidated.value = false;
  Object.keys(validationErrors.value).forEach((key) => {
    validationErrors.value[key] = false;
  });
}

async function submitForm() {
  if (validateForm()) {
    const submissionData = { ...formData.value };

    if (submissionData.course_id && submissionData.course_id.id) {
      submissionData.course_id = submissionData.course_id.id;
    }

    if (submissionData.device_class_id && submissionData.device_class_id.id) {
      submissionData.device_class_id = submissionData.device_class_id.id;
    }

    if (submissionData.device_type_id && submissionData.device_type_id.id) {
      submissionData.device_type_id = submissionData.device_type_id.id;
    }

    if (submissionData.provider_id && submissionData.provider_id.id) {
      submissionData.provider_id = submissionData.provider_id.id;
    }

    try {
      loading.value = true;
      await store.dispatch("devices/addDevice", submissionData);

      store.dispatch("toastNotification/displayToast", {
        message: `Device ${formData.value.tag} added successfully`,
        type: "success",
      });

      loading.value = false;

      formData.value = JSON.parse(JSON.stringify(deviceInitialData));
      closeModal();
    } catch (error) {
      loading.value = false;
      _error.value = error?.msg || "Failed to add device";

      store.dispatch("toastNotification/displayToast", {
        message: error?.msg || "Failed to add device",
        type: "error",
      });
    }
  }
}
</script>

<template>
  <div
    class="modal fade"
    :class="{ 'show d-block': modelValue }"
    tabindex="-1"
    role="dialog"
    aria-labelledby="deviceModalLabel"
    :aria-hidden="!modelValue"
  >
    <div
      v-if="modelValue"
      @click="closeModal"
      class="modal-backdrop fade show"
    ></div>

    <div class="modal-dialog" role="document">
      <div class="modal-content bg-dark">
        <div class="modal-header">
          <h5 class="modal-title" id="deviceModalLabel">Add Device</h5>
          <button
            type="button"
            class="close text-white"
            @click="closeModal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitForm">
            <div class="form-group mb-3">
              <label for="tag">Tag Number</label>
              <input
                type="text"
                class="form-control bg-dark text-white"
                :class="{
                  'border-danger-validation':
                    isFormValidated && validationErrors.tag,
                }"
                id="tag"
                v-model="formData.tag"
                @input="validateField('tag')"
                placeholder="Enter Tag Number"
              />
              <small
                v-if="isFormValidated && validationErrors.tag"
                class="text-danger"
              >
                Tag Number is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="imei">IMEI</label>
              <input
                type="text"
                class="form-control bg-dark text-white"
                :class="{
                  'border-danger-validation':
                    isFormValidated && validationErrors.imei,
                }"
                id="imei"
                v-model="formData.imei"
                @input="validateField('imei')"
                placeholder="Enter IMEI"
              />
              <small
                v-if="isFormValidated && validationErrors.imei"
                class="text-danger"
              >
                IMEI is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="iccid">ICCID</label>
              <input
                type="text"
                class="form-control bg-dark text-white"
                :class="{
                  'border-danger-validation':
                    isFormValidated && validationErrors.iccid,
                }"
                id="iccid"
                v-model="formData.iccid"
                @input="validateField('iccid')"
                placeholder="Enter ICCID"
              />
              <small
                v-if="isFormValidated && validationErrors.iccid"
                class="text-danger"
              >
                ICCID is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="msisdn">MSISDN</label>
              <input
                type="text"
                class="form-control bg-dark text-white"
                :class="{
                  'border-danger-validation':
                    isFormValidated && validationErrors.msisdn,
                }"
                id="msisdn"
                v-model="formData.msisdn"
                @input="validateField('msisdn')"
                placeholder="Enter MSISDN"
              />
              <small
                v-if="isFormValidated && validationErrors.msisdn"
                class="text-danger"
              >
                MSISDN is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="courseName">Course Name</label>
              <SearchableSelectComponent
                v-model="formData.course_id"
                @update:modelValue="validateField('course_id')"
                :options="courseNames"
                :label="'name'"
                :trackBy="'id'"
                :placeholder="'Select Course Name'"
                :class="{
                  'error-select': isFormValidated && validationErrors.course_id,
                }"
              />
              <small
                v-if="isFormValidated && validationErrors.course_id"
                class="text-danger"
              >
                Course Name is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="deviceClass">Device Class</label>
              <SearchableSelectComponent
                v-model="formData.device_class_id"
                @update:modelValue="validateField('device_class_id')"
                :options="deviceClasses"
                :label="'name'"
                :trackBy="'id'"
                :placeholder="'Select Device Class'"
                :class="{
                  'error-select':
                    isFormValidated && validationErrors.device_class_id,
                }"
              />
              <small
                v-if="isFormValidated && validationErrors.device_class_id"
                class="text-danger"
              >
                Device Class is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="deviceType">Device Type</label>
              <SearchableSelectComponent
                v-model="formData.device_type_id"
                @update:modelValue="validateField('device_type_id')"
                :options="deviceTypes"
                :label="'name'"
                :trackBy="'id'"
                :placeholder="'Select Device Type'"
                :class="{
                  'error-select':
                    isFormValidated && validationErrors.device_type_id,
                }"
              />
              <small
                v-if="isFormValidated && validationErrors.device_type_id"
                class="text-danger"
              >
                Device Type is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="provider">Providers</label>
              <SearchableSelectComponent
                v-model="formData.provider_id"
                @update:modelValue="validateField('provider_id')"
                :options="providers"
                :label="'name'"
                :trackBy="'id'"
                :placeholder="'Select Provider'"
                :class="{
                  'error-select':
                    isFormValidated && validationErrors.provider_id,
                }"
              />
              <small
                v-if="isFormValidated && validationErrors.provider_id"
                class="text-danger"
              >
                Provider is required
              </small>
            </div>

            <p v-if="_error" class="text-danger">
              {{ _error }}
            </p>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="closeModal">
            CLOSE
          </button>
          <button
            v-if="!loading"
            type="button"
            class="btn btn-success"
            @click="submitForm"
          >
            ADD DEVICE
          </button>
          <div
            v-if="loading"
            class="justify-content-center align-items-center"
            style="text-align: center"
          >
            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import "@/styles/modal.css";
@import "@/styles/deviceform.css";
</style>
